import { addDiffUnits, toTargetUnit } from '../utils/add-diff-units'
import { cleanUpRound, getAverage } from '../utils/numbers'
import { selectCaseSummary } from './case-summary'

const propsToKeep = [
  'sedationProviderName',
  'calculateDrugWaste', // needed for calculating wasted
  'surgeonName',
  'recorderName',
  'sedationLevel',
  'preMedsAdministered',
  'drugsAdministered',
  'drugDosage',
  'parenDrugs',
  'oralSedatives',
  'localAnesthetics',
  'infusionDrugs',
  'laDilutions',
  'events',
  'customEvents',
]
export const removePropsNotNeededForAnalytics = caseData => {
  const toReturn = {}
  for (const prop of propsToKeep) {
    if (caseData.hasOwnProperty(prop)) {
      toReturn[prop] = caseData[prop]
    }
  }
  return toReturn
}

const timeKeys = [
  'anesthesiaMinutes',
  'surgeryMinutes',
  'recoveryMinutes',
  'completeCaseMinutes',
  'monitoringMinutes',
]
/** @type {(entries: CaseSummary[]) => CaseMinutes} */
const getTimeAverages = entries => {
  const averagesData = {
    anesthesiaMinutes: [],
    surgeryMinutes: [],
    recoveryMinutes: [],
    completeCaseMinutes: [],
    monitoringMinutes: [],
  }
  for (const entry of entries) {
    for (const key of timeKeys) {
      const value = entry[key]
      // there are certain cases we don't want to include a case at all in the average
      // in those scenarios, the value should be explicitly set to null
      if (value !== null) {
        averagesData[key].push(entry[key])
      }
    }
  }
  const averages = {}
  for (const key in averagesData) {
    averages[key] = getAverage(averagesData[key])
  }
  // @ts-ignore
  return averages
}

const ensureDrugsHaveName = list => {
  for (const entry of list) {
    if (!entry.name) {
      throw Error('MISSING_DRUG_NAME')
    }
  }
}

/** @type {(entries: CaseReport[]) => CaseSummary[]} */
export const getProcessedCaseList = entries =>
  entries.map(({ caseData, ...rest }) => {
    try {
      const toReturn = {
        ...rest,
        ...selectCaseSummary(caseData),
      }
      ensureDrugsHaveName(toReturn.parenDrugsUsed)
      ensureDrugsHaveName(toReturn.oralSedativesUsed)
      return toReturn
    } catch (err) {
      const { compositionId, appointmentId } = rest
      const message = `${err.message} (apptId: ${appointmentId}, compId: ${compositionId})`
      err.message = message
      throw err
    }
  })

/** @type {(entries: CaseSummary[]) => OralDrugAnalysisResult[]} */
const getOralDrugAnalysis = entries => {
  const oralSedatives = {}
  const totalCaseCount = entries.length

  /** @type {(totalResultForCase: SingleCaseSingleOralDrugTotal) => void} */
  const addEntry = totalResultForCase => {
    const { id, total, maxDose, name, unit, byRoute } = totalResultForCase
    let existing = oralSedatives[id]
    if (!existing) {
      existing = oralSedatives[id] = {
        byRoute: {
          PO: 0,
          SL: 0,
        },
      }
    }

    const startingUnit = existing.unit || unit

    // this will determine the unit we're using from now on
    const { amount: updatedTotal, unit: updatedUnit } = addDiffUnits(
      existing.total || 0,
      startingUnit,
      total || 0,
      unit
    )

    existing.maxDose = Math.max(
      toTargetUnit(existing.maxDose || 0, startingUnit, updatedUnit),
      toTargetUnit(maxDose || 0, unit, updatedUnit)
    )
    existing.name = existing.name || name
    existing.unit = updatedUnit
    existing.total = updatedTotal
    existing.caseCount = (existing.caseCount || 0) + 1
    existing.percentOfCases = cleanUpRound(existing.caseCount / totalCaseCount)
    existing.byRoute = {
      PO: addDiffUnits(
        existing.byRoute.PO,
        startingUnit,
        byRoute.PO,
        unit,
        updatedUnit
      ).amount,
      SL: addDiffUnits(
        existing.byRoute.SL,
        startingUnit,
        byRoute.SL,
        unit,
        updatedUnit
      ).amount,
    }
  }

  for (const entry of entries) {
    for (const drugEntry of entry.oralSedativesUsed) {
      addEntry(drugEntry)
    }
  }

  const asArray = []

  for (const id in oralSedatives) {
    asArray.push({ id, ...oralSedatives[id] })
  }

  asArray.sort((a, b) => b.caseCount - a.caseCount)

  return asArray
}

/** @type {(entries: CaseSummary[]) => ParenDrugAnalysisResult[]} */
const getParenDrugAnalysis = entries => {
  const parenDrugs = {}
  const totalCaseCount = entries.length

  /** @type {(totalResultForCase: SingleCaseSingleParenDrugTotal) => void} */
  const addEntry = totalResultForCase => {
    const {
      id,
      total,
      wasted,
      maxDose,
      name,
      unit,
      byRoute,
    } = totalResultForCase
    if (!parenDrugs[id]) {
      parenDrugs[id] = {
        byRoute: {
          IV: 0,
          IM: 0,
          IN: 0,
        },
      }
    }

    const existing = parenDrugs[id]
    const startingUnit = existing.unit || unit

    // this will determine the unit we're using from now on
    const { amount: updatedTotal, unit: updatedUnit } = addDiffUnits(
      existing.total || 0,
      startingUnit,
      total || 0,
      unit
    )

    // this will force wasted to use the same unit
    const { amount: updatedWaste } = addDiffUnits(
      existing.wasted || 0,
      startingUnit,
      wasted || 0,
      unit,
      updatedUnit
    )

    existing.maxDose = Math.max(
      toTargetUnit(existing.maxDose || 0, startingUnit, updatedUnit),
      toTargetUnit(maxDose || 0, unit, updatedUnit)
    )
    existing.name = existing.name || name
    existing.unit = updatedUnit
    existing.total = updatedTotal
    existing.wasted = updatedWaste
    existing.expended = cleanUpRound(updatedTotal + updatedWaste)
    existing.caseCount = (existing.caseCount || 0) + 1
    existing.percentOfCases = cleanUpRound(existing.caseCount / totalCaseCount)
    existing.byRoute = {
      IV: addDiffUnits(
        existing.byRoute.IV,
        startingUnit,
        byRoute.IV,
        unit,
        updatedUnit
      ).amount,
      IM: addDiffUnits(
        existing.byRoute.IM,
        startingUnit,
        byRoute.IM,
        unit,
        updatedUnit
      ).amount,
      IN: addDiffUnits(
        existing.byRoute.IN,
        startingUnit,
        byRoute.IN,
        unit,
        updatedUnit
      ).amount,
    }
  }

  for (const entry of entries) {
    for (const drugEntry of entry.parenDrugsUsed) {
      addEntry(drugEntry)
    }
  }

  const asArray = []

  for (const id in parenDrugs) {
    asArray.push({ id, ...parenDrugs[id] })
  }

  asArray.sort((a, b) => b.caseCount - a.caseCount)

  return asArray
}

/** @type {(entries: CaseReport[]) => CaseAnalyticsResult} */
export const getCaseAnalytics = entries => {
  const cases = getProcessedCaseList(entries)
  return {
    cases,
    timeAverages: getTimeAverages(cases),
    oralDrugs: getOralDrugAnalysis(cases),
    parenDrugs: getParenDrugAnalysis(cases),
  }
}
